// ── Users ("/users") — the user directory (Wave 1) ─────────────────────────
// The users list is a server-backed collection. Search, role/status filters,
// and pagination are sent to the API so the page never pretends a bounded
// client-side slice is the complete organization roster.
//
// ROLE-GATING: the /users route is STAFF-ONLY (super_admin / admin) at the route
// manifest — company_admin, coach, cohort_lead, and participant get
// generic-not-found before this page mounts, and a denied READ renders
// permission-denied (never empty). Within staff, granting the `admin` role
// requires super_admin (enforced in the form's role picker + server re-check).
//
// Affordances render ONLY when the read is READY (posture null) or honest-EMPTY;
// a hard-denied read shows ONLY the honest state — no create/edit/deactivate
// controls over a refused surface.

import { Button, CollectionToolbar, IconUsers, ProTable, Switch, useToast } from "@vjal/ui";
import { useCallback, useMemo, useState } from "react";

import { HonestState } from "../../components/honest-state";
import { honestGateFromError, postureForCoachingError, referenceFromError } from "../../services/coaching-error-posture";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { useUserLifecycleMutations } from "../../hooks/useCoachingQueries";
import { evaluateActionCapability } from "../../domain/capabilities";
import type { CoachingUserDto } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import { useUsersCollection, type UserRow, type UsersCollectionFetcher } from "./users-collection";
import { UserFormModal, type UserFormValues } from "./user-form-modal";
import { userWriteBody } from "./users-write";
import { UserDeactivateModal } from "./user-deactivate-modal";

const services = getCoachingServices();

export function UsersPage({ role, routePath }: ProductScreenContext) {
  const toast = useToast();
  const mutations = useUserLifecycleMutations();

  const allowCreate = evaluateActionCapability(role, "users:create").allowed;
  const allowUpdate = evaluateActionCapability(role, "users:update").allowed;

  const [showDeactivated, setShowDeactivated] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CoachingUserDto | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<CoachingUserDto | null>(null);

  const fetchUsers = useCallback<UsersCollectionFetcher>(async ({ page, pageSize, q, filters, sort }) => {
    const response = await services.users.list({
      page,
      per_page: pageSize,
      q: q.trim() || undefined,
      role: typeof filters.role === "string" && filters.role ? filters.role : undefined,
      status: typeof filters.status === "string" && filters.status ? filters.status : undefined,
      sort: sort ? `${sort.id}:${sort.dir}` : undefined,
      include_deactivated: showDeactivated ? "true" : undefined
    });
    return { rows: response.items as UserRow[], total: response.total ?? response.items.length };
  }, [showDeactivated]);

  const collectionActions = useMemo(
    () => ({
      allowUpdate,
      onEdit: setEditTarget,
      onDeactivate: setDeactivateTarget
    }),
    [allowUpdate]
  );
  const collection = useUsersCollection(fetchUsers, collectionActions, routePath);
  const hasScopedView = Boolean(collection.state.q.trim()) || collection.activeFilters.length > 0 || collection.state.page > 1;
  const noRowsOnScopedPage = hasScopedView && collection.uiState === "ready" && collection.rows.length === 0;
  const displayCollection = noRowsOnScopedPage
    ? ({ ...collection, uiState: "empty" } as typeof collection)
    : collection;
  const posture = collection.uiState === "loading" || collection.state.status === "idle"
    ? "loading"
    : collection.uiState === "error"
      ? postureForCoachingError(collection.state.error)
      : collection.total === 0 && !hasScopedView
        ? "empty"
        : null;
  const gated = posture !== null;
  const showToolbar = posture === null || posture === "empty";

  function submitCreate(values: UserFormValues) {
    return mutations.create
      .mutateAsync(userWriteBody(values))
      .then((user) => {
        setCreateOpen(false);
        mutations.create.reset();
        collection.refresh();
        toast.success(user.outcome === "reused"
          ? `Existing VJAL user ${user.name ?? values.name} added`
          : user.outcome === "restored"
            ? `Existing VJAL user ${user.name ?? values.name} restored`
            : `User ${user.name ?? values.name} created`);
        return user;
      });
  }

  function submitEdit(values: UserFormValues) {
    if (!editTarget) return Promise.reject(new Error("No user selected"));
    return mutations.update
      .mutateAsync({ userId: editTarget.id, body: userWriteBody(values) })
      .then((user) => {
        setEditTarget(null);
        mutations.update.reset();
        collection.refresh();
        toast.success(`User ${user.name ?? values.name} updated`);
        return user;
      });
  }

  function submitDeactivate() {
    if (!deactivateTarget) return Promise.reject(new Error("No user selected"));
    return mutations.deactivate.mutateAsync(deactivateTarget.id).then((result) => {
      setDeactivateTarget(null);
      mutations.deactivate.reset();
      collection.refresh();
      toast.success(`User ${deactivateTarget.name ?? deactivateTarget.email ?? ""} deactivated`);
      return result;
    });
  }

  return (
    <section aria-label="Users" className="col-span-12 flex flex-col gap-4" data-testid="users-page">
      {showToolbar && gated ? (
        <div className="space-y-3">
          <CollectionToolbar
            collection={collection}
            searchPlaceholder="Search by name or email…"
            primaryAction={
              allowCreate ? (
                <Button variant="primary" onClick={() => setCreateOpen(true)}>
                  Add user
                </Button>
              ) : undefined
            }
            className="rounded-xl border border-line"
            stableChipNames
          />
          <Switch label="Show deactivated users" checked={showDeactivated} onChange={(checked) => { setShowDeactivated(checked); collection.refresh(); }} />
        </div>
      ) : null}

      {showToolbar && !gated ? (
        <Switch label="Show deactivated users" checked={showDeactivated} onChange={(checked) => { setShowDeactivated(checked); collection.refresh(); }} />
      ) : null}

      <div className="rounded-xl border border-line bg-white">
        {gated ? (
          <HonestState
            posture={posture ?? "loading"}
            gate={honestGateFromError(collection.state.error)}
            reference={referenceFromError(collection.state.error)}
            onRetry={collection.refresh}
            icon={<IconUsers size={22} />}
            canCreate={allowCreate && !showToolbar}
            title={posture === "empty" ? "No users yet" : undefined}
            message={
              posture === "empty"
                ? allowCreate
                  ? "Add the first staff or coach user to start assigning scope."
                  : "Users will appear here once they are created."
                : undefined
            }
            createAction={
              allowCreate ? (
                <Button variant="primary" onClick={() => setCreateOpen(true)}>
                  Add user
                </Button>
              ) : undefined
            }
          />
        ) : (
          /* F3: real @vjal/ui ProTable-1 recipe export (not marker-only table chrome). */
          <ProTable
            collection={displayCollection}
            primaryAction={
              allowCreate ? (
                <Button variant="primary" onClick={() => setCreateOpen(true)}>
                  Add user
                </Button>
              ) : undefined
            }
            className="p-0"
            tableProps={{
              caption: "Users in the current view",
              emptyTitle: hasScopedView ? "No users match your search" : "No users yet",
              emptyMessage: hasScopedView
                ? `No matching users on this page. ${collection.total} total user${collection.total === 1 ? "" : "s"} match the current filters.`
                : "Add a user to start assigning scope.",
              tableClassName: "min-w-[920px]",
            }}
            stableChipNames
          />
        )}
      </div>

      {allowCreate && (
        <UserFormModal
          open={createOpen}
          mode="create"
          actorRole={role}
          onClose={() => {
            setCreateOpen(false);
            mutations.create.reset();
          }}
          onSubmit={submitCreate}
          submitting={mutations.create.isPending}
          submitError={mutations.create.error}
        />
      )}

      {allowUpdate && (
        <>
          <UserFormModal
            open={editTarget !== null}
            mode="edit"
            actorRole={role}
            user={editTarget}
            onClose={() => {
              setEditTarget(null);
              mutations.update.reset();
            }}
            onSubmit={submitEdit}
            submitting={mutations.update.isPending}
            submitError={mutations.update.error}
          />
          <UserDeactivateModal
            open={deactivateTarget !== null}
            user={deactivateTarget}
            onClose={() => {
              setDeactivateTarget(null);
              mutations.deactivate.reset();
            }}
            onConfirm={submitDeactivate}
            submitting={mutations.deactivate.isPending}
            submitError={mutations.deactivate.error}
          />
        </>
      )}
    </section>
  );
}
